import type { BestPracticeData } from '@deltek/specification-client/core/Models/bestPractice'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'

const useStyles = makeStyles(() => ({
    root: {
        fontSize: '1.1rem',
        lineHeight: 1.4,
        minWidth: '180px',
    },
    row: {
        marginBottom: '2px',
    },
    label: {
        fontWeight: 600,
    },
    placeholder: {
        fontStyle: 'italic',
        opacity: 0.85,
    },
}))

interface TooltipRow {
    label: string
    value: string
}

const buildRows = (
    bp: BestPracticeData
): { paramRows: TooltipRow[]; extraRows: TooltipRow[] } => {
    const paramRows: TooltipRow[] = (bp.parameters || [])
        .map((p) => {
            const label = p.modifierList?.name?.trim()
            const values = (p.selectedModifiers || [])
                .map((m) => m.name)
                .filter(Boolean)
                .join(', ')
            if (!label || !values) return null
            return { label, value: values }
        })
        .filter((r): r is TooltipRow => Boolean(r))

    const extraRows: TooltipRow[] = []
    if (bp.projectGroup) {
        extraRows.push({ label: 'Project Group', value: bp.projectGroup })
    }
    const loc = bp.location || {}
    const locationParts = [loc.city, loc.region, loc.country].filter(Boolean)
    if (locationParts.length > 0) {
        extraRows.push({ label: 'Location', value: locationParts.join(', ') })
    }

    return { paramRows, extraRows }
}

// Merges parameters across multiple BPs, combining values for the same modifier
// list name (e.g. two BPs both with "Owner Type" → "owner 1, owner 2").
const buildMergedRows = (
    bps: BestPracticeData[]
): { paramRows: TooltipRow[]; extraRows: TooltipRow[] } => {
    const paramMap = new Map<string, string[]>()
    for (const bp of bps) {
        for (const p of bp.parameters ?? []) {
            const label = p.modifierList?.name?.trim()
            if (!label) continue
            if (!paramMap.has(label)) paramMap.set(label, [])
            for (const m of p.selectedModifiers ?? []) {
                if (m.name && !paramMap.get(label)!.includes(m.name)) {
                    paramMap.get(label)!.push(m.name)
                }
            }
        }
    }

    const paramRows: TooltipRow[] = []
    for (const [label, values] of paramMap) {
        if (values.length === 0) continue
        paramRows.push({ label, value: values.join(', ') })
    }

    const extraRows: TooltipRow[] = []
    const groups = [
        ...new Set(
            bps
                .map((bp) => bp.projectGroup)
                .filter((g): g is string => Boolean(g))
        ),
    ]
    if (groups.length > 0) {
        extraRows.push({ label: 'Project Group', value: groups.join(', ') })
    }
    const locations = [
        ...new Set(
            bps
                .map((bp) => {
                    const loc = bp.location ?? {}
                    return [loc.city, loc.region, loc.country]
                        .filter(Boolean)
                        .join(', ')
                })
                .filter(Boolean)
        ),
    ]
    if (locations.length > 0) {
        extraRows.push({ label: 'Location', value: locations.join('; ') })
    }

    return { paramRows, extraRows }
}

// Count of badge-eligible rows: parameter rows only (project group + location
// are shown in the tooltip and now included in the count by design).
// For multiple BPs the count reflects merged unique modifier list names.
export const countParameterRows = (
    bp: BestPracticeData | BestPracticeData[] | null | undefined
): number => {
    if (!bp) return 0
    if (Array.isArray(bp)) {
        const { paramRows, extraRows } = buildMergedRows(bp)
        return paramRows.length + extraRows.length
    }
    const { paramRows, extraRows } = buildRows(bp)
    return paramRows.length + extraRows.length
}

interface BPTooltipContentProps {
    data: BestPracticeData | BestPracticeData[] | null
    loading: boolean
    error: boolean
}

export const BPTooltipContent = ({
    data,
    loading,
    error,
}: BPTooltipContentProps) => {
    const classes = useStyles()

    if (loading) {
        return (
            <div className={classes.root}>
                <span className={classes.placeholder}>Loading…</span>
            </div>
        )
    }
    if (error) {
        return (
            <div className={classes.root}>
                <span className={classes.placeholder}>Failed to load</span>
            </div>
        )
    }
    if (!data || (Array.isArray(data) && data.length === 0)) {
        return (
            <div className={classes.root}>
                <span className={classes.placeholder}>
                    No Best Practice details
                </span>
            </div>
        )
    }

    const { paramRows, extraRows } = Array.isArray(data)
        ? buildMergedRows(data)
        : buildRows(data)
    const allRows = [...extraRows, ...paramRows]

    if (allRows.length === 0) {
        return (
            <div className={classes.root}>
                <span className={classes.placeholder}>
                    Best Practice has no parameters
                </span>
            </div>
        )
    }

    return (
        <div className={classes.root}>
            {allRows.map((row) => (
                <div key={row.label} className={classes.row}>
                    <span className={classes.label}>{row.label}:</span>{' '}
                    {row.value}
                </div>
            ))}
        </div>
    )
}
